import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Home } from 'lucide-react';
import { Button } from '@/components/ui';

const content = {
  hr: {
    title: 'Stranica nije pronađena',
    body: 'Ova stranica ne postoji ili je premještena.',
    home: 'Na naslovnicu',
    blog: 'Savjeti za iznajmljivače',
  },
  en: {
    title: 'Page not found',
    body: "This page doesn't exist or has moved.",
    home: 'Go to homepage',
    blog: 'Tips for hosts',
  },
};

/**
 * Catch-all for unknown URLs. Cloudflare's SPA fallback answers them with
 * status 200 and the homepage HTML, so without `noindex` Google files every
 * mistyped or stale link as a "soft 404" copy of the homepage. Google's
 * documented fix for client-rendered apps is a noindex added by JavaScript.
 */
export default function NotFoundPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4">
      <Helmet>
        <title>{`${t.title} | BookiApp`}</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="max-w-md w-full text-center py-16">
        <Link to="/" className="inline-flex mb-8">
          <img src="/favicon-original.png" alt="BookiApp" className="h-10 w-auto" />
        </Link>
        <p className="text-sm font-semibold tracking-widest text-[hsl(var(--muted-foreground))] mb-2">404</p>
        <h1 className="text-3xl font-bold tracking-tight mb-3">{t.title}</h1>
        <p className="text-[hsl(var(--muted-foreground))] mb-8">{t.body}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/">
            <Button className="w-full sm:w-auto gap-2">
              <Home className="h-4 w-4" />
              {t.home}
            </Button>
          </Link>
          <Link to={`/${lang}/blog/`}>
            <Button variant="outline" className="w-full sm:w-auto gap-2">
              {t.blog}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
